import React, { useEffect, useMemo, useState, Fragment, createElement } from 'react';
import { CheckCircle2Icon, Clock3Icon, SettingsIcon, ShieldCheckIcon, StarIcon, UsersIcon } from 'lucide-react';
import { AccordionItem } from '../components/AccordionItem';
import { BuyButton, checkoutUrl } from '../components/BuyButton';
import { useScreenInit } from '../useScreenInit';
const productImage = 'https://cdn.magicpatterns.com/uploads/5QGC1Fn6MnrTsDyXFupfeB/ChatGPT_Image_Jul_10,_2026,_06_28_30_PM.png';
const modules = [['🎯 Volume 1 — Fundamentals and Technical Movements of the Game', 'Applied functional anatomy, mechanics of each shot (dink, drive, serve, third shot drop, volley), footwork, and self-assessment of technique.'], ['🛡️ Volume 2 — Injury Prevention by Specific Movement', 'Protocol by injury: Pickleball Elbow, shoulder, Achilles, knee, low back. Movement → injury → protocol reference table.'], ['💪 Volume 3 — 12-Week Strength and Mobility Program', '3-phase periodization, week-by-week routines, warm-up/cooldown, and age-based adjustment.'], ['🧓 Volume 4 — Pickleball for Older Adults: Special Considerations', 'Bone density, fall prevention, osteoporosis, medication effects on performance, plus a practical checklist.'], ['🔁 Volume 5 — Returning to Play After an Injury', 'Progressive return to play, protocols by injury, green/yellow/red checklist, and medical clearance vs. actually ready.'], ['⚡ Volume 6 — Explosive Power and Game-Specific Speed', 'Lower/upper body power, plyometrics adapted for recreational players, and an 8-week program.'], ['🔋 Volume 7 — Endurance and In-Match Competitive Capacity', 'Anaerobic-intermittent conditioning, between-point/between-match recovery, and basic tournament nutrition.'], ['🧠 Volume 8 — Reaction Speed, Agility, and Decision-Making Under Pressure', '50 pages of multidirectional agility, visual/auditory reaction training, first-step speed, and decision-making under fatigue.']];
const bonusPreviews = ['https://cdn.magicpatterns.com/uploads/5mFaMMp5jdU2zEyrvjEr9z/Bono_1_.png', 'https://cdn.magicpatterns.com/uploads/oYnMyhJBn2Rqn2xWRiBJhy/Bono_2.png', 'https://cdn.magicpatterns.com/uploads/ojmEsTnrFnNucqeqEhKvuL/Bono_3.png', 'https://cdn.magicpatterns.com/uploads/8f8gwkdBKadmBzgem47caG/Bono_4_.png', 'https://cdn.magicpatterns.com/uploads/6cDiLzBAF2zjuo67VyxGqG/Bono_5.png', 'https://cdn.magicpatterns.com/uploads/1W7V56sBydm7DEWCmBXJYB/Bono_6.png', 'https://cdn.magicpatterns.com/uploads/3xRb2YPQ6JQPDavq5BATRv/Bono_7_.png', 'https://cdn.magicpatterns.com/uploads/vty6SPcFrZ6Ywh9CRkRHG9/Bono_8.png'];
const bonuses = [['📊 Personal Risk Calculator', 'Interactive Excel — Anchor: Volume 2, entry point for the whole kit.'], ['🗓️ Your First 4 Weeks', 'PDF — Anchor: overall kit sequencing.'], ['📈 Safe Play Frequency Calculator', 'Interactive Excel — Anchor: Volume 2/3, safe play frequency.'], ['🌿 7-Day Active Recovery Plan', 'PDF — Anchor: response to overplaying.'], ['📉 12-Week Progression Planner', 'Excel + auto-chart — Anchor: Volume 3, load tracking.'], ['🚀 Power & Speed Assessment', 'Interactive Excel — Anchor: Volume 6, performance entry point.'], ['🥤 Match Day Fuel & Recovery Planner', 'PDF — Anchor: Volume 7, tournaments/leagues.'], ['⚡ 6-Week Reaction & Agility Tracker', 'Excel + auto-chart — Anchor: Volume 8, agility/reaction tracking.']];
const testimonials = [['Mike R.', '52, Naples, FL', 'I stopped guessing what to do before playing. My knees feel better and I move with much more confidence.'], ['Lisa M.', '47, Austin, TX', 'The warm-ups and movement cues made a huge difference. I’m playing sharper without feeling beat up after.'], ['David T.', '61, Scottsdale, AZ', 'Finally, a plan that respects the fact that I’m not 25 anymore but still want to compete.'], ['Karen B.', '56, Raleigh, NC', 'The return-to-play checklist was exactly what I needed after a stubborn Achilles flare-up.'], ['James P.', '44, San Diego, CA', 'Clear, practical, and built around real pickleball. The power work has improved my first step already.']];
const problems = ['Your elbow or shoulder starts talking after long dinking sessions.', 'You feel stiff, slow, or unstable when the game speeds up.', 'You play through knee, Achilles, or low-back discomfort because you don’t want to miss a match.', 'You know you need strength work—but don’t know what actually transfers to the court.', 'You want to compete harder without turning every game into a recovery problem.'];
export function PickleballLanding() {
  useScreenInit();
  const [seconds, setSeconds] = useState(9 * 60 + 42);
  const [seats, setSeats] = useState(317);
  const [notice, setNotice] = useState(0);
  useEffect(() => {
    const ticker = window.setInterval(() => setSeconds(value => value > 0 ? value - 1 : 9 * 60 + 42), 1000);
    const seatsTicker = window.setInterval(() => setSeats(value => value > 286 ? value - 1 : 317), 19000);
    const noticeTicker = window.setInterval(() => setNotice(value => (value + 1) % 3), 9000);
    return () => {
      window.clearInterval(ticker);
      window.clearInterval(seatsTicker);
      window.clearInterval(noticeTicker);
    };
  }, []);
  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','1252242806993118');fbq('track','PageView');`;
    document.head.appendChild(script);
    return () => script.remove();
  }, []);
  const clock = useMemo(() => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`, [seconds]);
  const purchases = ['Ashley from Denver, CO', 'Mark from Tampa, FL', 'Cynthia from Phoenix, AZ'];
  return <main className="min-h-screen bg-slate-950 text-white" data-id="element-11">
      <div className="court-bg" aria-hidden="true" data-id="element-12" />
      <div className="relative mx-auto w-full max-w-md overflow-hidden px-5 pb-10" data-id="element-13">
        <header className="py-6 text-center" data-id="element-14">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-yellow-300" data-id="element-15">
            Pickleball Pro 360
          </p>
          <h1 className="mt-3 font-display text-4xl font-black leading-[0.97] tracking-tight" data-id="element-16">
            Stronger Shots.
            <br data-id="element-17" />
            Safer Joints.
            <br data-id="element-18" />
            <span className="text-teal-300" data-id="element-19">Same You, Better Game.</span>
          </h1>
        </header>

        <a href="#checkout" aria-label="View the Pickleball Pro 360 offer" data-id="element-20">
          <img src={productImage} alt="Pickleball Pro 360 digital kit" className="w-full rounded-2xl border border-white/15 shadow-2xl shadow-black/50" data-id="element-21" />
        </a>

        <section className="py-7 text-center" data-id="element-22">
          <p className="mx-auto max-w-sm text-base font-semibold leading-snug text-slate-700" data-id="element-23">
            The complete injury-prevention and performance system for pickleball
            players.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-bold text-sky-800" data-id="element-24">
            <span data-id="element-25">📄 100% PDF</span>
            <span className="text-sky-300" data-id="element-26">•</span>
            <span data-id="element-27">⚡ Instant Delivery</span>
            <span className="text-sky-300" data-id="element-28">•</span>
            <span data-id="element-29">📚 8 Modules + 8 Bonuses</span>
          </div>
          <div className="mt-5" data-id="element-30">
            <BuyButton data-id="element-31">Master Pickleball Now</BuyButton>
          </div>
        </section>

        <section className="section-card problem-card" data-id="element-32">
          <h2 className="font-display text-2xl font-black leading-tight" data-id="element-33">
            If you play pickleball regularly, this has probably happened to you:
          </h2>
          <ul className="mt-5 space-y-3" data-id="element-34">
            {problems.map(problem => <li className="flex gap-3 text-sm leading-relaxed text-slate-700" key={problem} data-id="element-35">
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" data-id="element-36" />
                {problem}
              </li>)}
          </ul>
        </section>

        <section className="mt-6" data-id="element-37">
          <h2 className="text-center font-display text-2xl font-black" data-id="element-38">
            That’s why we built{' '}
            <span className="text-teal-300" data-id="element-39">Pickleball Pro 360.</span>
          </h2>
          <p className="mx-auto mt-2 max-w-xs text-center text-sm text-slate-300" data-id="element-40">
            Eight practical volumes to help you play longer, move better, and
            train with intention.
          </p>
          <div className="mt-5 space-y-3" data-id="element-41">
            {modules.map(([title, content], index) => <AccordionItem key={title} title={title} index={index} data-id="element-42">
                {content}
              </AccordionItem>)}
          </div>
          <div className="mt-5" data-id="element-43">
            <BuyButton compact data-id="element-44">Get the Complete System</BuyButton>
          </div>
        </section>

        <section className="mt-10" data-id="element-45">
          <h2 className="text-center font-display text-2xl font-black" data-id="element-46">
            Built for players who want to stay in the game.
          </h2>
          <div className="mt-5 space-y-3" data-id="element-47">
            {testimonials.map(([name, location, quote]) => <article className="testimonial-card rounded-xl p-4" key={name} data-id="element-48">
                <div className="flex gap-0.5 text-yellow-300" aria-label="5 out of 5 stars" data-id="element-49">
                  {Array.from({
                length: 5
              }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-current" data-id="element-50" />)}
                </div>
                <p className="mt-2 text-sm italic leading-relaxed text-white" data-id="element-51">
                  “{quote}”
                </p>
                <p className="mt-3 text-xs font-bold text-white" data-id="element-52">
                  {name} · {location}
                </p>
              </article>)}
          </div>
        </section>

        <section className="mt-10" data-id="element-53">
          <p className="text-center text-xs font-black uppercase tracking-[0.18em] text-yellow-300" data-id="element-54">
            Free today only
          </p>
          <h2 className="mt-2 text-center font-display text-2xl font-black" data-id="element-55">
            We want you to truly stand out in pickleball, so we’re giving you:
          </h2>
          <div className="mt-5 space-y-3" data-id="element-56">
            {bonuses.map(([title, content], index) => <AccordionItem key={title} title={title} index={index + 20} previewImage={bonusPreviews[index]} data-id="element-57">
                {content}
              </AccordionItem>)}
          </div>
        </section>

        <section className="section-card mt-9" data-id="element-58">
          <h2 className="text-center font-display text-2xl font-black" data-id="element-59">
            The difference is in your game plan.
          </h2>
          <div className="mt-5 grid grid-cols-2 overflow-hidden rounded-xl border border-sky-200 text-center text-xs" data-id="element-60">
            <div className="without-header p-3 font-black text-white" data-id="element-61">
              WITHOUT
              <br data-id="element-62" />
              PICKLEBALL PRO 360
            </div>
            <div className="with-header p-3 font-black text-white" data-id="element-63">
              WITH
              <br data-id="element-64" />
              PICKLEBALL PRO 360
            </div>
            {[['Random warm-ups', 'Court-ready preparation'], ['Push through pain', 'Know when & how to adapt'], ['Generic workouts', 'Pickleball-specific training'], ['Hope for consistency', 'A 12-week progression']].map(([without, withPlan]) => <Fragment key={without} data-id="element-65">
                <div className="without-cell border-t p-3" data-id="element-66">✕ {without}</div>
                <div className="with-cell border-l border-t p-3" data-id="element-67">
                  ✓ {withPlan}
                </div>
              </Fragment>)}
          </div>
        </section>

        <section id="checkout" className="final-offer mt-10 rounded-2xl p-5 shadow-xl" data-id="element-68">
          <p className="text-xs font-black uppercase tracking-[0.17em] text-sky-700" data-id="element-69">
            Today’s complete offer
          </p>
          <h2 className="mt-1 font-display text-3xl font-black text-slate-900" data-id="element-70">
            Pickleball Pro 360
          </h2>
          <p className="mt-1 text-sm font-medium text-slate-600" data-id="element-71">
            One complete system. Everything you need to play stronger and
            smarter.
          </p>

          <div className="order-summary mt-5 text-left" data-id="element-72">
            <p className="text-xs font-black uppercase tracking-[0.13em] text-sky-800" data-id="element-73">
              Your digital order includes
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm font-semibold text-slate-700" data-id="element-74">
              <span data-id="element-75">✓ 8 training volumes</span>
              <span data-id="element-76">✓ 8 practical bonuses</span>
              <span data-id="element-77">✓ Strength & mobility plan</span>
              <span data-id="element-78">✓ Injury-prevention tools</span>
              <span data-id="element-79">✓ Trackers & calculators</span>
              <span data-id="element-80">✓ Instant PDF access</span>
            </div>
          </div>

          <div className="price-panel mt-4 p-4" data-id="element-81">
            <p className="text-sm font-medium text-slate-500 line-through" data-id="element-82">
              $72 USD
            </p>
            <p className="font-display text-5xl font-black text-sky-700" data-id="element-83">
              $25 <span className="text-lg" data-id="element-84">USD</span>
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-600" data-id="element-85">
              One payment · Lifetime digital access
            </p>
          </div>

          <div className="mt-4 flex items-center justify-center gap-2 text-sm font-black text-red-700" data-id="element-86">
            <Clock3Icon className="h-5 w-5" data-id="element-87" /> Offer resets in {clock}
          </div>
          <div className="mt-3" data-id="element-88">
            <div className="mb-1 flex justify-between text-xs font-semibold text-slate-600" data-id="element-89">
              <span data-id="element-90">
                <UsersIcon className="mr-1 inline h-4 w-4" data-id="element-91" />
                Limited spots
              </span>
              <span data-id="element-92">{seats} / 1,200 remaining</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-sky-100" data-id="element-93">
              <div className="h-full rounded-full bg-red-500" style={{
              width: `${seats / 1200 * 100}%`
            }} data-id="element-94" />
            </div>
          </div>
          <div className="mt-5" data-id="element-95">
            <BuyButton data-id="element-96">Yes — I Want the Complete System</BuyButton>
          </div>
          <p className="mt-4 flex items-center justify-center gap-2 text-xs font-medium text-slate-600" data-id="element-97">
            <ShieldCheckIcon className="h-4 w-4 text-emerald-600" data-id="element-98" />
            Secure checkout · Download immediately
          </p>
        </section>

        <section className="mt-9" data-id="element-99">
          <h2 className="text-center font-display text-2xl font-black" data-id="element-100">
            Questions before you start?
          </h2>
          <div className="mt-4 space-y-3" data-id="element-101">
            <AccordionItem title="Is this for beginners or advanced players?" index={30} data-id="element-102">
              It’s designed for recreational and competitive players at every
              level. You’ll learn the foundations, then use the progressions
              that match where you are.
            </AccordionItem>
            <AccordionItem title="I’m over 40—will the program be too intense?" index={31} data-id="element-103">
              No. The system includes age-based adjustments, mobility options,
              and a dedicated section on playing smart as an older adult.
            </AccordionItem>
            <AccordionItem title="Is this medical advice?" index={32} data-id="element-104">
              No. It is an education and performance resource. If you have an
              injury or medical concern, consult your qualified healthcare
              professional.
            </AccordionItem>
          </div>
        </section>

        <footer className="py-10 text-center text-sm text-slate-300" data-id="element-105">
          <SettingsIcon className="mx-auto mb-2 h-5 w-5 text-yellow-300" aria-hidden="true" data-id="element-106" />
          <p data-id="element-107">
            Need help?{' '}
            <a className="font-bold text-teal-300 underline" href="mailto:EbookDreamShop@gmail.com" data-id="element-108">
              EbookDreamShop@gmail.com
            </a>
          </p>
        </footer>
      </div>

      <div className="fixed bottom-4 left-4 right-4 z-20 mx-auto max-w-sm rounded-xl border border-teal-300/30 bg-slate-950/95 p-3 shadow-2xl backdrop-blur" data-id="element-109">
        <p className="text-xs text-slate-200" data-id="element-110">
          <span className="font-bold text-teal-300" data-id="element-111">{purchases[notice]}</span>{' '}
          just got instant access to Pickleball Pro 360.
        </p>
      </div>
    </main>;
}